import { useSyncExternalStore } from "react";
import { DefaultRecordingTemplates } from "@/common/enums/enums";
import type { Template } from "@/common/types/templates";
import { builtInTemplateLabel } from "@/lib/document-name";
import { DEFAULT_TEMPLATE_KEY } from "@/lib/onboarding";

// Defaults live on the account. Local defaults are kept only for the
// one-time move and failed onboarding saves; favourites stay per browser.

export const BUILT_IN_TEMPLATES: readonly string[] = Object.values(
  DefaultRecordingTemplates,
);
export const FALLBACK_TEMPLATE: string = DefaultRecordingTemplates.SOAP;

type Account = string | null | undefined;
const DEFAULT_PREFIX = "rosc:templates:default:";
const FAVORITES_PREFIX = "rosc:templates:favorites:";
const MAX_FAVORITES = 50;

const get = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const set = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode): the choice lasts for this page.
  }
};

const listeners = new Set<() => void>();
let version = 0;
const emit = (): void => {
  version++;
  for (const listener of listeners) listener();
};
const onStorage = (event: StorageEvent): void => {
  if (
    event.key === null ||
    event.key === DEFAULT_TEMPLATE_KEY ||
    event.key.startsWith(DEFAULT_PREFIX) ||
    event.key.startsWith(FAVORITES_PREFIX)
  )
    emit();
};
const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
};
const getVersion = (): number => version;

// Re-renders when a preference changes here or in another tab.
export const useTemplatePreferenceVersion = (): number =>
  useSyncExternalStore(subscribe, getVersion, getVersion);

export const isBuiltInTemplate = (value: string): boolean =>
  BUILT_IN_TEMPLATES.includes(value);

// Before public ids, templates were saved by numeric id; those can't be
// mapped to a template any more.
const isLegacyNumericId = (value: string | null): boolean =>
  !!value && /^[0-9]+$/.test(value);

const readRawLocalDefault = (account: Account): string | null =>
  (account ? get(`${DEFAULT_PREFIX}${account}`) : null) ??
  get(DEFAULT_TEMPLATE_KEY);

// The default saved in this browser. The account key wins; the onboarding key
// (one per browser, written before defaults were per account) is still read
// so nobody loses their choice. Old numeric template ids are ignored.
export const readLocalDefault = (account: Account): string | null => {
  const value = readRawLocalDefault(account);
  return value && !isLegacyNumericId(value) ? value : null;
};

export const writeLocalDefault = (account: Account, value: string): void => {
  if (account) set(`${DEFAULT_PREFIX}${account}`, value);
  else set(DEFAULT_TEMPLATE_KEY, value);
  // The browser-wide onboarding key must not shadow a later choice.
  if (account) set(DEFAULT_TEMPLATE_KEY, null);
  emit();
};

export const removeLocalDefault = (account: Account): void => {
  if (account) set(`${DEFAULT_PREFIX}${account}`, null);
  set(DEFAULT_TEMPLATE_KEY, null);
  emit();
};

export const resolveDefaultTemplate = (
  templates: readonly Pick<Template, "public_id" | "isDefault">[],
  _account: Account,
): string => {
  const server = templates.find((template) => template.isDefault === 1);
  if (server) return String(server.public_id);
  return FALLBACK_TEMPLATE;
};

export const readFavorites = (account: Account): string[] => {
  if (!account) return [];
  try {
    const value: unknown = JSON.parse(
      get(`${FAVORITES_PREFIX}${account}`) ?? "[]",
    );
    return Array.isArray(value)
      ? value
          .filter((item): item is string => typeof item === "string")
          .slice(0, MAX_FAVORITES)
      : [];
  } catch {
    return [];
  }
};

export const toggleFavorite = (account: Account, id: string): void => {
  if (!account) return;
  const current = readFavorites(account);
  const next = current.includes(id)
    ? current.filter((item) => item !== id)
    : [...current, id].slice(-MAX_FAVORITES);
  set(`${FAVORITES_PREFIX}${account}`, JSON.stringify(next));
  emit();
};

export type TemplateChoice = {
  // A built-in template name or a custom template public id.
  value: string;
  name: string;
  builtIn: boolean;
  template?: Template;
};

// Every template a recording can use, built-in first, in list order.
export const templateChoices = (
  templates: readonly Template[],
): TemplateChoice[] => [
  // Built-ins keep their English name as the value; the name shown is
  // translated.
  ...BUILT_IN_TEMPLATES.map((name) => ({
    value: name,
    name: builtInTemplateLabel(name),
    builtIn: true,
  })),
  ...templates.map((template) => ({
    value: String(template.public_id),
    name: template.name,
    builtIn: false,
    template,
  })),
];

// Favourites first (as their own group), then the rest.
export const splitFavorites = <T extends { value: string }>(
  choices: readonly T[],
  favorites: readonly string[],
): { favorites: T[]; others: T[] } => ({
  favorites: choices.filter((choice) => favorites.includes(choice.value)),
  others: choices.filter((choice) => !favorites.includes(choice.value)),
});
